import type { ContratProDTO } from '@/features/api/extranet/dto/Demande/CPRO'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { idccAutoriseTT } from '@/features/domaine/regles-akto/shared'
import ContratContent from '@/features/interface/demande/cpro/steps/contrat/ContratStep.vue'
import DocumentsContent from '@/features/interface/demande/cpro/steps/documents/DocumentsStep.vue'
import EmployeurContent from '@/features/interface/demande/cpro/steps/employeur/EmployeurStep.vue'
import FormationContent from '@/features/interface/demande/cpro/steps/formation/FormationStep.vue'
import IntroductionContent from '@/features/interface/demande/cpro/steps/introduction/IntroductionStep.vue'
import SalarieContent from '@/features/interface/demande/cpro/steps/salarie/SalarieStep.vue'
import SyntheseContent from '@/features/interface/demande/cpro/steps/synthese/SyntheseStep.vue'
import { CPRO_ACTIONS } from '@/features/interface/demande/cpro/useDemandeCPROActions'
import { useDemandeCPROActions } from '@/features/interface/demande/cpro/useDemandeCPROActions.ts'
import { useDemandeCPROStore } from '@/features/interface/demande/cpro/useDemandeCPROStore.ts'
import type { SetupDemande } from '@/features/interface/demande/shared/components/DemandeContent/DemandeContent.types.ts'
import { storeToRefs } from 'pinia'
import { type Component, provide } from 'vue'
import FinancementContent from './steps/financement/FinancementStep.vue'
import { CPRO_MODULES_ACTIONS, useModuleActions } from './steps/formation/useModuleActions'

type TabKey =
  | 'Introduction'
  | 'Employeur'
  | 'Salarié'
  | 'Contrat'
  | 'Formation'
  | 'Financement'
  | 'Synthèse'
  | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionContent,
  Employeur: EmployeurContent,
  Salarié: SalarieContent,
  Contrat: ContratContent,
  Formation: FormationContent,
  Financement: FinancementContent,
  Synthèse: SyntheseContent,
  Documents: DocumentsContent,
} as const

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ContratProDTO> = {
  Introduction: { include: (state) => [state.validationNotice] },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Salarié: {
    includeDeep: (state) => [state.salarie, state.tutorat],
  },
  Contrat: {
    includeDeep: (state) => [state.contrat],
    include: (state) => [state.dispositif, state.sousDispositif],
  },
  Formation: { includeDeep: (state) => [state.formation] },
  Financement: {
    includeDeep: (state) => [state.financement],
  },
  Synthèse: {
    include: (state) => [state.lieuSignature, state.commentaire, state.acceptationCg, state.attestationSurHonneur],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
} as const

export const SetupDemandeCPRO: SetupDemande<'MODEL_CPRO', TabKey> = {
  useStore: useDemandeCPROStore,
  injectActions: () => {
    const actions = useDemandeCPROActions()
    provide(CPRO_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (demande) => {
    const store = useDemandeCPROStore()
    const { typeContextCourant } = storeToRefs(store)
    if (
      idccAutoriseTT.includes(demande.employeur?.etablissement?.idcc?.code ?? '') &&
      typeContextCourant.value === TypesContextesAutorisesEnum.Adherent
    )
      return TABS_ERR_CONFIG
    const { Financement, ...newTabs } = TABS_ERR_CONFIG
    return newTabs
  },
  getNatureFichiers,
  onInititalize(_actions) {
    provide(CPRO_MODULES_ACTIONS, useModuleActions())
  },
} as const

function getNatureFichiers() {
  const store = useDemandeCPROStore()
  const { model, lov } = storeToRefs(store)

  return lov.value.NatureFichier.map((item) => {
    switch (item.code) {
      case 'AttestationFormateurInterne': {
        return ['ContratProfessionnalisation', 'ContratProfessionnalisationHope'].includes(model.value!.sousDispositif!)
          ? {
              code: 'AttestationFormateurInterne',
              intitule: 'Attestation Service Formation interne + CV des formateurs',
            }
          : {
              code: 'AttestationFormateurInterne',
              intitule: 'Attestation Service Formation interne',
            }
      }
      case 'Convention': {
        return {
          code: 'Convention',
          intitule: 'Convention signée des 2 parties',
        }
      }
      default: {
        return item
      }
    }
  })
}
